// Interactive 3D scene of the screw connector, built with react-three-fiber like the lock calculator: an
// orthographic camera orbited by OrbitControls, the reference plane under the connection, the scene ready in groups
// (element 1 / element 2 / screws / forces) and the HTML toolbar kept outside the Canvas. The geometry itself is not
// rebuilt here: `buildScrewScene` produces the same Three.js data the report-capture viewer uses, and the canvas
// renders it with that viewer's colour pipeline (linear output, no tone mapping, same lights): each element keeps its
// own colour and the screen matches the PDF.
import { OrbitControls, OrthographicCamera } from "@react-three/drei";
import { Canvas, useThree } from "@react-three/fiber";
import { Suspense, memo, useEffect, useLayoutEffect, useMemo, useRef, type FC } from "react";
import * as THREE from "three";
import { registerCapturableCanvas } from "../../../../utils/webglCanvases";
import { elementOpacity, type BuiltScrewScene, type ElementMode } from "../view3dScene";
import { VIEWER_LIGHTS, linearColor, type ViewName } from "../tc3dViewer";
import ScrewDimensions from "./ScrewDimensions";
import ScrewGrid from "./ScrewGrid";
import { CAMERA_DISTANCE, FRAME_MARGIN, MIN_SCENE_RADIUS, VIEWER_BACKGROUND, VIEW_DIRECTIONS } from "./viewerConfig";

export type ScrewSceneVisibility = {
  element1: ElementMode;
  element2: ElementMode;
  screws: boolean;
  forces: boolean;
  grid: boolean;
};

const AMBIENT_COLOR = linearColor(VIEWER_LIGHTS.ambient.color);
const MAIN_LIGHT_COLOR = linearColor(VIEWER_LIGHTS.main.color);
const FILL_LIGHT_COLOR = linearColor(VIEWER_LIGHTS.fill.color);

export type ViewPresetRequest = {
  view: ViewName;
  // increments on every click, so re-applying the same preset still moves the camera
  seq: number;
};

export type ScrewSceneProps = {
  build: BuiltScrewScene | null;
  visibility: ScrewSceneVisibility;
  showQuote: boolean;
  viewPreset: ViewPresetRequest | null;
  // changes only when a new context deserves a fresh automatic framing (connection type), not on every input change
  autoFrameKey: string;
};

const ScrewScene: FC<ScrewSceneProps> = ({ build, visibility, showQuote, viewPreset, autoFrameKey }) => {
  // Pivot of OrbitControls: the centre the camera rig frames, so a rebuild keeps orbiting around the connection
  // instead of moving the pivot elsewhere. Set here too, so the pivot is right even before that effect has run.
  const controlsTarget = useMemo<[number, number, number] | undefined>(() => {
    if (!build || build.bounds.isEmpty()) return undefined;
    const center = build.bounds.getCenter(new THREE.Vector3());
    return [center.x, center.y, center.z];
  }, [build]);

  return (
    <Canvas
      linear
      flat
      frameloop="demand"
      dpr={[1, 2]}
      gl={{ antialias: true }}
      style={{ background: VIEWER_BACKGROUND }}
      camera={{ position: [0.4, 0.3, 0.4], fov: 40, near: 0.001, far: 100 }}
      onCreated={registerCapturableCanvas}
    >
      <OrthographicCamera makeDefault near={0.001} far={100} />
      <ambientLight color={AMBIENT_COLOR} intensity={VIEWER_LIGHTS.ambient.intensity} />
      <directionalLight
        color={MAIN_LIGHT_COLOR}
        position={VIEWER_LIGHTS.main.position}
        intensity={VIEWER_LIGHTS.main.intensity}
      />
      <directionalLight
        color={FILL_LIGHT_COLOR}
        position={VIEWER_LIGHTS.fill.position}
        intensity={VIEWER_LIGHTS.fill.intensity}
      />
      <Suspense fallback={null}>
        <SceneContent build={build} visibility={visibility} showQuote={showQuote} />
      </Suspense>
      <CameraRig build={build} viewPreset={viewPreset} autoFrameKey={autoFrameKey} />
      <OrbitControls
        makeDefault
        target={controlsTarget ?? [0, 0, 0]}
        enableDamping
        dampingFactor={0.15}
        minDistance={0.02}
        maxDistance={20}
        rotateSpeed={0.8}
        panSpeed={1}
      />
    </Canvas>
  );
};

const SceneContent: FC<{
  build: BuiltScrewScene | null;
  visibility: ScrewSceneVisibility;
  showQuote: boolean;
}> = memo(({ build, visibility, showQuote }) => {
  const invalidate = useThree((state) => state.invalidate);
  // The root stays mounted as a whole, so disposing it later reaches every group; the toolbar only flips the
  // visibility of the groups and the opacity of the elements' materials, without rebuilding the scene.
  useLayoutEffect(() => {
    if (!build) return;
    applyElementMode(build.element1, visibility.element1, showQuote);
    applyElementMode(build.element2, visibility.element2, showQuote);
    build.screws.visible = visibility.screws;
    build.forces.visible = visibility.forces;
    invalidate();
  }, [build, visibility, showQuote, invalidate]);

  if (!build) return null;
  return (
    <>
      {visibility.grid && !build.bounds.isEmpty() ? (
        <ScrewGrid bounds={build.bounds} layout={build.layout} />
      ) : null}
      <primitive object={build.root} />
      <ScrewDimensions commands={build.dims} showLines={showQuote} showForces={visibility.forces} />
    </>
  );
});
SceneContent.displayName = "SceneContent";

// The builder's rule (elementOpacity, as in the report viewer); hidden removes the element.
function applyElementMode(element: THREE.Group, mode: ElementMode, showQuote: boolean): void {
  element.visible = mode !== "hidden";
  const opacity = elementOpacity(mode, showQuote);
  element.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      // switching between opaque and transparent changes the shader program
      if (material.transparent !== opacity < 1) material.needsUpdate = true;
      material.transparent = opacity < 1;
      material.opacity = opacity;
    }
  });
}

// Frames the connection on first data, when the connection type changes and on every View menu click. It deliberately
// does not re-frame on ordinary input changes, so the user's zoom and rotation survive recalculation.
const CameraRig: FC<{
  build: BuiltScrewScene | null;
  viewPreset: ViewPresetRequest | null;
  autoFrameKey: string;
}> = ({ build, viewPreset, autoFrameKey }) => {
  const { camera, controls, size, invalidate } = useThree();
  const lastSeq = useRef(-1);
  const lastAutoKey = useRef<string | null>(null);
  const hadBuild = useRef(false);

  useEffect(() => {
    if (!build) {
      hadBuild.current = false;
      return;
    }
    const isPreset = !!viewPreset && viewPreset.seq !== lastSeq.current;
    const isFirst = !hadBuild.current;
    const isNewContext = lastAutoKey.current !== null && lastAutoKey.current !== autoFrameKey;
    const needsControls = lastAutoKey.current === null;
    hadBuild.current = true;
    if (!isPreset && !isFirst && !isNewContext && !needsControls) return;
    // The first effect can run before R3F has measured the canvas (height 0): do not set lastAutoKey, or the
    // framing would be lost with a zero zoom and never applied again.
    if (size.height <= 0) return;

    if (build.bounds.isEmpty()) return;
    const sphere = build.bounds.getBoundingSphere(new THREE.Sphere());
    if (!Number.isFinite(sphere.radius) || sphere.radius <= 0) return;

    const target = sphere.center.clone();
    const radius = Math.max(sphere.radius, MIN_SCENE_RADIUS);
    const direction = new THREE.Vector3(...VIEW_DIRECTIONS[viewPreset?.view ?? "iso"]).normalize();
    const distance = Math.max(radius * 8, CAMERA_DISTANCE);
    const orthoCamera = camera as THREE.OrthographicCamera;
    // Orthographic zoom is "pixels per world unit", so the scene height is what sets the scale.
    orthoCamera.zoom = size.height / (2 * radius * FRAME_MARGIN);
    orthoCamera.updateProjectionMatrix();
    camera.position.copy(target).addScaledVector(direction, distance);
    camera.lookAt(target);

    if (controls) {
      const orbit = controls as unknown as { target: THREE.Vector3; update(): void };
      orbit.target.copy(target);
      orbit.update();
      // Only with controls mounted the pivot is actually applied; otherwise leave the key unset so the effect runs
      // again as soon as OrbitControls registers itself (first mount) and frames the pivot too.
      lastAutoKey.current = autoFrameKey;
    }
    if (isPreset) lastSeq.current = viewPreset!.seq;
    invalidate();
  }, [build, viewPreset, autoFrameKey, camera, controls, size.height, invalidate]);

  return null;
};

export default memo(ScrewScene);
